<template>
    <section class="mt-6 flex w-full flex-col">
        <div class="flex w-full items-center justify-between">
            <button
                type="button"
                class="lg:hidden"
                @click="appStore.side = !appStore.side"
            >
                <SvgIcon
                    name="menu"
                    class="h-6 w-6"
                />
            </button>
            <div class="ml-4 mr-auto hidden items-center gap-3 md:flex lg:ml-0 lg:mr-0">
                <HeaderNavTitle
                    :show-history-back-button="showHistoryBackButton"
                    :show-return-to-order-link="showReturnToOrderLink"
                    :return-to-order-route="returnToOrderRoute"
                    :header-title="headerTitle"
                    :order-ref="order.getOrderRef"
                    :order-state="order.data?.state"
                    :order-status-formatted="order.data?.status_formatted"
                    @go-back="goBackToPreviousPage"
                    @set-active-tab="setOrderViewActiveTabOnReturn"
                />
            </div>
            <div class="relative flex w-fit shrink-0 items-center gap-x-4">
                <template v-if="userStore.isTenantContext">
                    <RoleGuard :guarded="['ADMIN', 'SALES', 'OPS', 'MANAGER', 'FINANCE', 'OWNER', 'SUPER']">
                        <BookingStatus />
                        <CommandPalette />
                        <router-link
                            class="relative items-center gap-x-1 lg:flex"
                            to="/inbox/calls"
                        >
                            <SvgIcon
                                name="system-missed-call"
                                class="size-6 text-muval-gray-1 hover:text-muval-gray-2"
                            />
                            <div
                                v-show="badgeCount.phone > 0"
                                class="absolute -right-3 -top-3"
                            >
                                <span
                                    :class="[
                                        'z-1 flex items-center justify-center bg-brand text-xs text-white',
                                        badgeCount.phone >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="badgeCount.phone"
                                />
                                <span
                                    :class="[
                                        'absolute top-0 -z-1 flex animate-ping items-center justify-center bg-brand text-xs text-white',
                                        badgeCount.phone >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="' '"
                                />
                            </div>
                        </router-link>
                        <router-link
                            class="relative items-center gap-x-1 lg:flex"
                            to="/inbox/sms"
                        >
                            <SvgIcon
                                name="system-inbox"
                                class="size-6 text-muval-gray-1 hover:text-muval-gray-2"
                            />
                            <div
                                v-show="badgeCount.text > 0"
                                class="absolute -right-3 -top-3"
                            >
                                <span
                                    :class="[
                                        'z-1 flex items-center justify-center bg-brand text-xs text-white',
                                        badgeCount.text >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="badgeCount.text"
                                />
                                <span
                                    :class="[
                                        'absolute top-0 -z-1 flex animate-ping items-center justify-center bg-brand text-xs text-white',
                                        badgeCount.text >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="' '"
                                />
                            </div>
                        </router-link>
                        <button
                            type="button"
                            class="relative items-center gap-x-1 lg:flex"
                            aria-label="My order actions"
                            @click="myOrderActionsOpen = true"
                        >
                            <SvgIcon
                                name="system-pace"
                                class="size-5 text-muval-gray-1 hover:text-muval-gray-2"
                            />
                            <div
                                v-show="visitorUnreadCount > 0"
                                class="absolute -right-3 -top-3"
                            >
                                <span
                                    :class="[
                                        'z-1 flex items-center justify-center bg-brand text-xs text-white',
                                        visitorUnreadCount >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="visitorUnreadCount"
                                />
                                <span
                                    :class="[
                                        'absolute top-0 -z-1 flex animate-ping items-center justify-center bg-brand text-xs text-white',
                                        visitorUnreadCount >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="' '"
                                />
                            </div>
                        </button>
                    </RoleGuard>
                    <NotificationIcon />
                </template>
                <!-- Profile dropdown -->
                <ProfileDropDown />
                <UserInactiveModal v-if="!userStore.isMoveproSuperAdminUser" />
                <UserActivityMonitor v-if="!userStore.isMoveproSuperAdminUser" />
            </div>
        </div>
        <MyOrderActionsModal v-model="myOrderActionsOpen" />
        <div class="mr-auto mt-4 flex w-full items-center gap-3 md:hidden">
            <HeaderNavTitle
                :show-history-back-button="showHistoryBackButton"
                :show-return-to-order-link="showReturnToOrderLink"
                :return-to-order-route="returnToOrderRoute"
                :header-title="headerTitle"
                :order-ref="order.getOrderRef"
                :order-state="order.data?.state"
                :order-status-formatted="order.data?.status_formatted"
                @go-back="goBackToPreviousPage"
                @set-active-tab="setOrderViewActiveTabOnReturn"
            />
        </div>

        <div
            id="new-top-header-bottom"
            class="w-full"
        />
    </section>
</template>
<script setup>
import useEmitter from '@plugins/useEmitter';
import { onMounted, onUnmounted } from 'vue';
import { startCase } from 'lodash';
import { useCompanyStore } from '@store/company';
import { useOrderStore } from '@store/order';
import { useNotificationsStore } from '@store/notifications';
import { capitalize } from '@utilities/filters';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

const appStore = useAppStore();
const userStore = useUserStore();
const jobStore = useJobStore();
const tenderStore = useTenderStore();
const notificationsStore = useNotificationsStore();

const visitorUnreadCount = computed(() => notificationsStore.getVisitorUnreadCount);

const breakpoints = useBreakpoints(breakpointsTailwind);
const desktopView = breakpoints.greaterOrEqual('sm');

const badgeCount = reactive({
    text: 0,
    phone: 0,
});

const myOrderActionsOpen = ref(false);

const getInboxCount = async () => {
    const { data: text } = await window.tenantAxios.get('inbox', {
        params: {
            count: 1,
            direction: 'INCOMING',
            seen: 0,
            type: 'TEXT',
            ...(userStore.getTeam != 'NONE' &&
                userStore.getTeam && {
                    team: userStore.getTeam,
                }),
        },
    });

    const { data: phone } = await window.tenantAxios.get('inbox', {
        params: {
            count: 1,
            direction: 'INCOMING',
            seen: 0,
            type: 'PHONE',
            ...(userStore.getTeam != 'NONE' &&
                userStore.getTeam && {
                    team: userStore.getTeam,
                }),
        },
    });

    badgeCount.text = text.count;
    badgeCount.phone = phone.count;
};

const emitter = useEmitter();

onMounted(() => {
    if (userStore.isCentralContext || userStore.isOnlyFieldStaff()) {
        return;
    }

    notificationsStore.fetchUnreadCount('visitor');
    getInboxCount();

    let stop = setInterval(getInboxCount, 60000);

    onUnmounted(() => {
        clearInterval(stop);
    });

    emitter.on('update:seen', getInboxCount);
});

const route = useRoute();
const router = useRouter();

const company = useCompanyStore();
const companyName = computed(() => {
    return company?.data?.name ?? null;
});

const order = useOrderStore();

const headerTitle = computed(() => {
    switch (route.name) {
        case 'Dashboard':
            // return userStore.getFirstName ? `Hi ${userStore.getFirstName}!` : null;
            return 'Dashboard';
        case 'Search':
            return `Search ${userStore.search ?? ''}`;
        // Interstate
        case 'InterstateTripCreate':
            return 'Create interstate trip';
        case 'InterstateTripEdit':
            return 'Edit interstate trip';
        case 'InterstateLaneCreate':
            return 'Create interstate lane';
        case 'InterstateLaneEdit':
            return 'Edit interstate lane';
        // Depots
        case 'DepotsCreate':
            return 'Create depot';
        case 'DepotsEdit':
            return `Edit depot`;
        // Companies
        case 'CompaniesView':
            return companyName.value;
        case 'CompaniesEdit':
            return 'Edit company profile';
        case 'CompaniesPreferences':
            return 'Company preferences';
        // Local
        case 'LocalCreate':
            return 'Create local';
        case 'LocalEdit':
            return 'Edit local';
        // Fleet
        case 'FleetCreate':
            return 'Create vehicle';
        case 'FleetEdit':
            return 'Edit vehicle';
        // Staff
        case 'StaffCreate':
            return 'Create team member';
        case 'StaffEdit':
            return 'Edit team member';
        case 'CompanyStaffEdit':
            return 'Edit team member';
        // SMS & CALL
        case 'InboxCalls':
            return 'Calls';
        case 'InboxSms':
            return 'Inbox';
        // Comms
        case 'CommunicationCreate':
            return 'Create template';
        case 'CommunicationEdit':
            return 'Edit template';
        // Jobs
        case 'JobView':
            return jobStore?.data?.order_ref ? `Job #${jobStore?.data?.order_ref ?? ''}` : '';
        // Quotes
        case 'QuoteView':
            return tenderStore?.tender?.order_ref ? `Quote #${tenderStore?.tender?.order_ref ?? ''}` : '';
        // ICR
        case 'OrderInventoryConditionReport':
            return desktopView.value ? 'Inventory condition report' : 'ICR';
        case 'IcrSignOff':
            return 'ICR sign off';
        case 'IcrViewSignedOff':
            return route?.meta?.title;
        // Errors
        case '404Page':
            return '';
        default:
            return route?.meta?.title ?? capitalize(startCase(route.name));
    }
});

const showReturnToOrderLink = computed(() => {
    return ['OrderInventoryConditionReport', 'OrderInventory', 'IcrSignOff', 'IcrViewSignedOff'].includes(
        route.name ?? '',
    );
});

const showHistoryBackButton = computed(() => {
    return false;
});

const returnToOrderRoute = computed(() => {
    return { name: 'OrderView', params: { id: route.params.id } };
});

function setOrderViewActiveTabOnReturn() {
    try {
        const tab =
            ['OrderInventoryConditionReport', 'IcrSignOff', 'IcrViewSignedOff'].includes(route.name ?? '') ? 'ICR' : (
                'Inventory'
            );
        sessionStorage.setItem('orderView.activeTab', tab);
    } catch {
        // Intentionally ignore sessionStorage errors (e.g., in private mode)
    }
}

function goBackToPreviousPage() {
    router.push({
        name: 'IcrSignOff',
        params: { id: route.params.id },
    });
}
</script>
